import type { ComponentType, CSSProperties } from "react";
import type { BrandColorTokens } from "./palette";

// types.ts — the BrandThemeConfig contract.
//
// Deliberately narrow: a brand may change its COLOURS and its four brand
// ASSETS (primary logo, favicon, spark/reward icon, currency icon). Nothing
// else. There is no slot here for copy, routes, feature flags, currency codes
// or formatting — a white-label swap is a repaint, not a product variant, and
// keeping the type this small is what makes that reviewable.

export const BRAND_IDS = ["smartmarket", "richpoll"] as const;
export type BrandId = (typeof BRAND_IDS)[number];

/** The brand the app falls back to when configuration is missing or invalid. */
export const DEFAULT_BRAND_ID: BrandId = "smartmarket";

/**
 * Props every brand mark accepts. Call sites size and place a mark exactly as
 * they always did (`className="h-4 w-4"`, `style={{ width: "0.92em" }}`); the
 * brand decides whether that box is filled by an inline SVG that inherits
 * `currentColor` or by a raster image with its own fixed colours.
 */
export interface BrandMarkProps {
  className?: string;
  style?: CSSProperties;
  "aria-hidden"?: boolean | "true" | "false";
  "aria-label"?: string;
}

export type BrandMark = ComponentType<BrandMarkProps>;

export interface BrandFavicon {
  href: string;
  /** MIME type for the `<link rel="icon">` tag. */
  type: string;
}

export interface BrandAssets {
  /** Horizontal lockup — the desktop rail logo and the wide-tablet header. */
  logoWordmark: BrandMark;
  /** Vertical/compact lockup — tablet-portrait header. */
  logoStacked: BrandMark;
  /** Square icon-only mark, cropped to its artwork — phone header. */
  logoMark: BrandMark;
  /** Reward / brand glyph shown inline in chrome and on the auth screens. */
  sparkIcon: BrandMark;
  /**
   * The unit glyph that leads a money figure. Purely a picture: swapping it
   * changes nothing about the currency's code, label, precision or value —
   * see src/lib/money.ts, which owns all of that and knows nothing about brands.
   */
  currencyIcon: BrandMark;
  favicon: BrandFavicon;
  /**
   * Social-preview image (Open Graph / X card). Never rendered in the app —
   * it is what a crawler shows when someone shares a link, so it carries the
   * brand's own artwork and must not be inherited from another brand.
   */
  ogImage: string;
  /**
   * Optional promo badge pinned to the foot of the desktop rail. OPTIONAL on
   * purpose: the badge is brand artwork, so showing one brand's badge under
   * another would leak branding, and inventing artwork a brand has not
   * approved is not this card's to do. A brand without one renders no badge —
   * the rail's layout is unchanged either way.
   */
  welcomeBadge?: string | null;
}

export interface BrandThemeConfig {
  id: BrandId;
  /** Human-readable label for diagnostics and tests. NOT user-facing copy. */
  label: string;
  colors: BrandColorTokens;
  assets: BrandAssets;
}
